import { useCallback, useEffect, useRef, useState } from 'react';
import { useWorkspace } from '../contexts/ContextoWorkspace';
import { familiasService } from '../services/api/familias.service';

export interface Familia {
  id: string;
  codigo: string;
  nombre: string;
}

export interface FamiliaInput {
  codigo: string;
  nombre: string;
}

interface UseFamiliasReturn {
  familias: Familia[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  crearFamilia: (input: FamiliaInput) => Promise<Familia>;
}

export function useFamilias(): UseFamiliasReturn {
  const { empresaId } = useWorkspace();
  const [familias, setFamilias] = useState<Familia[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const esCargandoRef = useRef(false);

  const scopeReady = empresaId != null && empresaId.trim() !== '';

  const refresh = useCallback(async () => {
    if (!scopeReady || !empresaId) {
      setFamilias([]);
      return;
    }
    if (esCargandoRef.current) return;
    esCargandoRef.current = true;

    setLoading(true);
    setError(null);
    try {
      const response = await familiasService.getAll(empresaId);
      const mapped: Familia[] = (response.data || []).map((item) => ({
        id: item.id,
        codigo: item.codigo,
        nombre: item.nombre,
      }));
      setFamilias(mapped);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron cargar las familias');
    } finally {
      setLoading(false);
      esCargandoRef.current = false;
    }
  }, [empresaId, scopeReady]);

  useEffect(() => {
    void refresh();
  }, [empresaId, scopeReady]);

  const crearFamilia = useCallback(async (input: FamiliaInput): Promise<Familia> => {
    if (!scopeReady || !empresaId) throw new Error('Selecciona una empresa para registrar familias');
    setLoading(true);
    setError(null);
    try {
      const response = await familiasService.create({
        empresaId,
        codigo: input.codigo,
        nombre: input.nombre,
      });
      const created: Familia = {
        id: response.data.id,
        codigo: response.data.codigo,
        nombre: response.data.nombre,
      };
      setFamilias((prev) => [...prev, created]);
      return created;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudo crear la familia';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [empresaId, scopeReady]);

  return { familias, loading, error, refresh, crearFamilia };
}
